/**
 * EmailHtmlFrame - Sandboxed iframe for rendering HTML email content.
 *
 * Extraído de `src/modules/email/troia/components/TroiaReadingPane.tsx`
 * (Spec A Task 8 — Timeline V2 do ticket) para shared, SEM alterar o modelo
 * de segurança: mesmo `sandbox`, mesma CSP, mesmo `srcDoc`. Consumido por:
 * - `TroiaReadingPane.tsx` (corpo de e-mails da caixa de entrada)
 * - `TimelineEmailItem.tsx` (corpo de e-mails inbound na timeline do ticket)
 * - Plano B (Retenção) — mesmo componente, sem duplicar a lógica de sandbox.
 *
 * Security model (same as Close.com):
 * - sandbox without allow-scripts → JS completely blocked
 * - allow-popups → links open in new tab
 * - allow-popups-to-escape-sandbox → opened tabs work normally
 * - allow-same-origin → allows reading iframe height for auto-resize
 * - CSP script-src 'none' → defense-in-depth layer against JS
 * - NEVER use allow-scripts + allow-same-origin together
 *
 * Styling:
 * - Forces white background on body to prevent dark-themed emails
 * - All links open with target="_blank" and rel="noopener noreferrer"
 */
import React, { useRef, useEffect, useCallback } from 'react';

export interface EmailHtmlFrameProps {
  html: string;
}

export const EmailHtmlFrame: React.FC<EmailHtmlFrameProps> = ({ html }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const resize = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument?.body) return;
    iframe.style.height = iframe.contentDocument.body.scrollHeight + 'px';
  }, []);

  // Build the full srcdoc HTML with security headers and base styles.
  // Styles are LOW specificity defaults — the email's own inline styles
  // (which have higher specificity) will naturally override them.
  // This matches how Gmail/Outlook/Thunderbird render: white background
  // as fallback only, preserving the email's original design.
  const srcdoc = React.useMemo(() => {
    return `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="script-src 'none'">
<base target="_blank">
<style>
  body {
    margin: 0;
    padding: 16px;
    background-color: #ffffff;
    color: #1e293b;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  img { max-width: 100%; height: auto; }
  a { color: #8b5cf6; }
  blockquote {
    margin: 8px 0;
    padding: 8px 16px;
    border-left: 3px solid #e5e7eb;
    color: #64748b;
  }
</style>
</head><body>${html}</body></html>`;
  }, [html]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const onLoad = () => {
      // Auto-resize to content height
      resize();
      const doc = iframe.contentDocument;
      if (!doc?.body) return;
      // Resize when images load
      const images = doc.querySelectorAll('img');
      images.forEach((img) => img.addEventListener('load', resize));
      // Observe body size changes
      const observer = new ResizeObserver(resize);
      observer.observe(doc.body);
      return () => observer.disconnect();
    };

    iframe.addEventListener('load', onLoad);
    return () => iframe.removeEventListener('load', onLoad);
  }, [srcdoc, resize]);

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcdoc}
      className="w-full border-0 rounded-lg"
      style={{ minHeight: 60, backgroundColor: '#ffffff' }}
      title="Email content"
    />
  );
};

EmailHtmlFrame.displayName = 'EmailHtmlFrame';
